// yard/YardScene.jsx - Main scene with lighting, environment, and entity management
import React, { useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { OrbitControls, Sky } from '@react-three/drei';
import useYardStore from './yardStore';
import DogManager from './DogManager';
import Squirrel from './Squirrel';
import YardEnvironment from './YardEnvironment';

const YardScene = ({ deals, darkMode, onDogSelect }) => {
  const controlsRef = useRef();

  const initializeDogs = useYardStore(state => state.initializeDogs);
  const updateSquirrel = useYardStore(state => state.updateSquirrel);
  const updateDogsChase = useYardStore(state => state.updateDogsChase);
  const updateDogsReturn = useYardStore(state => state.updateDogsReturn);
  const squirrelEvent = useYardStore(state => state.squirrelEvent);
  const cameraState = useYardStore(state => state.camera);

  // Initialize dogs when deals change
  useEffect(() => {
    console.log('YardScene received deals:', deals?.length, deals);
    if (deals && deals.length > 0) {
      initializeDogs(deals);
    }
  }, [deals, initializeDogs]);

  // Animation loop
  useFrame((state, delta) => {
    // Cap delta to prevent jumps
    const cappedDelta = Math.min(delta, 0.1);

    // Update squirrel position
    if (squirrelEvent.active) {
      updateSquirrel(cappedDelta);
      updateDogsChase(cappedDelta);
    } else {
      updateDogsReturn(cappedDelta);
    }
  });

  // Update camera when focused dog changes
  useEffect(() => {
    if (cameraState.focusedDogId && controlsRef.current) {
      controlsRef.current.target.set(
        cameraState.target.x,
        cameraState.target.y,
        cameraState.target.z
      );
    }
  }, [cameraState]);

  return (
    <>
      {/* Sky with sun */}
      {!darkMode && (
        <Sky
          distance={450000}
          sunPosition={[100, 50, 100]}
          inclination={0.5}
          azimuth={0.25}
          rayleigh={0.5}
          turbidity={8}
        />
      )}

      {/* Bright lighting */}
      <ambientLight intensity={darkMode ? 0.5 : 0.7} />
      <directionalLight
        position={[20, 30, 10]}
        intensity={darkMode ? 1.2 : 1.5}
        castShadow
        shadow-mapSize={[1024, 1024]}
        shadow-camera-far={100}
        shadow-camera-left={-50}
        shadow-camera-right={50}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
      />
      <hemisphereLight
        skyColor={darkMode ? '#4a6fa5' : '#87ceeb'}
        groundColor={darkMode ? '#3d5a3e' : '#90EE90'}
        intensity={0.6}
      />
      {/* Extra fill light */}
      <pointLight position={[-20, 20, -20]} intensity={0.3} color="#ffffff" />

      {/* Environment and yard */}
      <YardEnvironment darkMode={darkMode} />

      {/* Dogs */}
      <DogManager darkMode={darkMode} onDogSelect={onDogSelect} />

      {/* Squirrel */}
      <Squirrel />

      {/* Camera controls */}
      <OrbitControls
        ref={controlsRef}
        enablePan={true}
        enableZoom={true}
        enableRotate={true}
        minDistance={10}
        maxDistance={80}
        maxPolarAngle={Math.PI / 2.2}
        target={[0, 0, 0]}
      />
    </>
  );
};

export default YardScene;
